import type { ParentAttendanceSummary, ParentChildAttendance } from '@repo/types/parent';

import { useGetChildrenAttendance } from '@repo/api/parent/useGetChildrenAttendance';
import { useGetChildrenAttendanceSummary } from '@repo/api/parent/useGetChildrenAttendanceSummary';
import { useGetMyChildren } from '@repo/api/parent/useGetMyChildren';
import { cn } from '@repo/lib/cn';
import { format, parseISO } from 'date-fns';
import { Dot } from 'lucide-react';

import { STATUS_CONFIG, STATUS_ORDER } from '@/components/features/groups/attendance/AttendanceTable';
import { ChildSectionList } from '@/components/features/parent/ChildSectionList';
import ParentPageHeader from '@/components/features/parent/layout/ParentPageHeader';
import { ParentListSection } from '@/components/features/parent/ParentListSection';
import { ParentListToolbar } from '@/components/features/parent/ParentListToolbar';
import { Badge } from '@/components/ui/badge';
import { useParentListParams } from '@/hooks/parent';

export default function ParentAttendancePage() {
  const { data: children = [], isPending: isPendingChildren, isError, errorMessage } = useGetMyChildren();
  const listParams = useParentListParams();

  const { results, failed, isPending } = useGetChildrenAttendance(children, listParams.buildParamsFor, !isPendingChildren);

  const summaries = useGetChildrenAttendanceSummary(children, !isPendingChildren);
  const summaryByChild = new Map(summaries.results.map(({ child, data }) => [child.id, data]));

  return (
    <div className={'base-direction-col'}>
      <div className={'flex w-full grow flex-col gap-6'}>
        <ParentPageHeader title={'Frekwencja'}>
          <ParentListToolbar childUsers={children} listParams={listParams} />
        </ParentPageHeader>

        <ChildSectionList
          results={results}
          failed={failed}
          isPending={isPendingChildren || isPending}
          isError={isError}
          errorMessage={errorMessage}
          summary={(data) => `Wyników: ${data.total}`}
          defaultExpanded={'none'}
        >
          {(data, child) => (
            <div className={'flex flex-col gap-3'}>
              <GlobalAttendanceSummary summary={summaryByChild.get(child.id)} />
              <ParentListSection childId={child.id} data={data} listParams={listParams} emptyMessage={'Brak zarejestrowanej obecności'}>
                {(records) => (
                  <ul className={'border-border-primary bg-background flex flex-col rounded-2xl border'}>
                    {records.map((record) => (
                      <AttendanceRecordRow key={`${record.meetingId}-${record.userId}`} record={record} />
                    ))}
                  </ul>
                )}
              </ParentListSection>
            </div>
          )}
        </ChildSectionList>
      </div>
    </div>
  );
}

function GlobalAttendanceSummary({ summary }: { summary: ParentAttendanceSummary | undefined }) {
  if (!summary) return null;

  return (
    <div className={'flex flex-wrap items-center gap-2'}>
      <Badge variant={'muted'} size={'sm'} border>
        Frekwencja ogólna: {summary.rate === null ? '—' : `${summary.rate}%`}
      </Badge>
      {STATUS_ORDER.map((status) => (
        <Badge key={status} variant={'muted'} size={'sm'} border>
          <Dot className={cn('-mx-1', STATUS_CONFIG[status].dotClassName)} />
          {STATUS_CONFIG[status].summaryLabel}: {summary[status]}
        </Badge>
      ))}
    </div>
  );
}

function AttendanceRecordRow({ record }: { record: ParentChildAttendance }) {
  const { meeting, status } = record;
  const statusConfig = status ? STATUS_CONFIG[status] : null;

  return (
    <li className={'border-border-primary/75 flex items-center gap-3 border-b px-4 py-2 last:border-b-0'}>
      <div className={'flex min-w-0 flex-col'}>
        <span className={'text-foreground truncate text-sm font-medium'}>{meeting.name}</span>
        <span className={'text-muted-foreground text-xs'}>{format(parseISO(meeting.startDate), 'd MMM yyyy, H:mm')}</span>
      </div>

      {statusConfig ? (
        <Badge size={'sm'} variant={'muted'} border className={cn('ms-auto', statusConfig.cellClassName)}>
          {statusConfig.label}
        </Badge>
      ) : (
        <span className={'text-muted-foreground ms-auto text-sm'}>—</span>
      )}
    </li>
  );
}
